import type { SupportedLocale } from '@/shared/i18n/locales';
import { loggedInTimestampCookieConfig, loginStateCookieConfig } from './config';

export interface LoginSnapshot {
  readonly _id?: string;
  readonly id?: string;
  readonly token?: string;
  readonly username?: string;
  readonly language?: string;
}

function canUseDocument() {
  return typeof document !== 'undefined';
}

function parseCookieEntries(cookieText: string) {
  return cookieText
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function writeCookieValue(
  config: {
    cookieName: string;
    domain: string;
    path: string;
    secure: boolean;
    maxAgeDays: number;
  },
  value: string,
) {
  if (!canUseDocument()) return;

  const expires = new Date();
  expires.setDate(expires.getDate() + config.maxAgeDays);

  document.cookie = [
    `${config.cookieName}=${value}`,
    `expires=${expires.toUTCString()}`,
    `domain=${config.domain}`,
    `path=${config.path}`,
    config.secure ? 'secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

function removeCookieValue(config: { cookieName: string; domain: string; path: string; secure: boolean }) {
  if (!canUseDocument()) return;

  document.cookie = [
    `${config.cookieName}=`,
    'expires=Thu, 01 Jan 1970 00:00:00 GMT',
    `domain=${config.domain}`,
    `path=${config.path}`,
    config.secure ? 'secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

export function readCookieValue(name: string) {
  if (!canUseDocument()) return null;

  const prefix = `${name}=`;
  const match = parseCookieEntries(document.cookie).find((entry) => entry.startsWith(prefix));

  return match ? match.slice(prefix.length) : null;
}

export function parseLoginSnapshot(rawValue: string | null) {
  if (!rawValue) return null;

  try {
    const value = decodeURIComponent(rawValue);
    return value ? (JSON.parse(value) as LoginSnapshot) : null;
  } catch {
    return null;
  }
}

export function readLoginSnapshot() {
  return parseLoginSnapshot(readCookieValue(loginStateCookieConfig.cookieName));
}

export function readLoggedInTimestamp() {
  return readCookieValue(loggedInTimestampCookieConfig.cookieName);
}

export function hasLoggedInTimestamp() {
  return Boolean(readLoggedInTimestamp());
}

export function readLoginLanguageHint() {
  const language = readLoginSnapshot()?.language;
  return typeof language === 'string' ? (language as SupportedLocale) : null;
}

export function writeLoginSnapshot(snapshot: LoginSnapshot) {
  const encodedValue = encodeURIComponent(JSON.stringify(snapshot));
  writeCookieValue(loginStateCookieConfig, encodedValue);
}

export function removeLoginSnapshot() {
  removeCookieValue(loginStateCookieConfig);
}

export function writeLoggedInTimestamp(timestamp = new Date().toISOString()) {
  writeCookieValue(loggedInTimestampCookieConfig, encodeURIComponent(timestamp));
}

export function removeLoggedInTimestamp() {
  removeCookieValue(loggedInTimestampCookieConfig);
}
